/**
 * Zustand store for care pass delegate sessions.
 * Separate from authStore — delegates don't have accounts.
 */

import { create } from 'zustand';
import i18n from '../i18n';
import {
  careApi,
  type CarePassDailyLog,
  type CarePassError,
  type CarePassErrorCode,
  type CarePassMedication,
} from '../lib/careApi';
import type { WeightDisplayUnit } from '../components/weight/types';
import { DEFAULT_WEIGHT_UNIT, isWeightUnit } from '../components/weight/useWeightUnitPref';

interface CarePassState {
  // Session data
  urlToken: string | null;
  catName: string | null;
  /** The cat's sex, for Portuguese agreement; null when unknown. */
  catSex: 'male' | 'female' | null;
  catId: string | null;
  /** This pass's id, so the check-in can tell the delegate's own care events apart
   *  from the owner's (only the former are theirs to remove). */
  carePassId: string | null;
  delegateName: string | null;
  scopes: string[];
  /** The OWNER's weight display unit, so the caregiver's weight screen reads and
   *  writes in the unit the owner speaks. Storage stays kg either way. */
  weightUnit: WeightDisplayUnit;
  startsAt: string | null;
  endsAt: string | null;
  isValidated: boolean;
  isLoading: boolean;
  error: string | null;
  /** Why the pass was refused, when the server said. A revoked or expired pass is
   *  an expected outcome with its own screen, not a fault to apologise for. */
  errorCode: CarePassErrorCode | null;

  // Cached data
  recentLogs: CarePassDailyLog[];
  medications: CarePassMedication[];

  // Actions
  validate: (urlToken: string) => Promise<void>;
  fetchRecentLogs: () => Promise<void>;
  fetchMedications: () => Promise<void>;
  clear: () => void;
}

export const useCarePassStore = create<CarePassState>((set, get) => ({
  urlToken: null,
  catName: null,
  catSex: null,
  catId: null,
  carePassId: null,
  delegateName: null,
  scopes: [],
  weightUnit: DEFAULT_WEIGHT_UNIT,
  startsAt: null,
  endsAt: null,
  isValidated: false,
  isLoading: false,
  error: null,
  errorCode: null,
  recentLogs: [],
  medications: [],

  validate: async (urlToken: string) => {
    set({ isLoading: true, error: null, errorCode: null });
    try {
      const data = await careApi.validateToken(urlToken);
      set({
        urlToken,
        catName: data.cat_name,
        catSex: data.cat_sex ?? null,
        catId: data.cat_id,
        carePassId: data.care_pass_id,
        delegateName: data.delegate_name,
        scopes: data.scopes,
        // An older backend that doesn't send the field leaves the caregiver on kg
        // rather than on something invented.
        weightUnit: isWeightUnit(data.weight_unit) ? data.weight_unit : DEFAULT_WEIGHT_UNIT,
        startsAt: data.starts_at,
        endsAt: data.ends_at,
        isValidated: true,
        isLoading: false,
      });
    } catch (err) {
      set({
        isLoading: false,
        isValidated: false,
        error: err instanceof Error ? err.message : i18n.t('carePass.store.validateFailed'),
        errorCode: (err as CarePassError)?.code ?? null,
      });
    }
  },

  fetchRecentLogs: async () => {
    const { urlToken } = get();
    if (!urlToken) return;
    try {
      const logs = await careApi.getRecentLogs(urlToken);
      set({ recentLogs: logs });
    } catch {
      // Non-critical — proceed without recent logs
    }
  },

  fetchMedications: async () => {
    const { urlToken } = get();
    if (!urlToken) return;
    try {
      const meds = await careApi.getMedications(urlToken);
      set({ medications: meds });
    } catch {
      // Non-critical
    }
  },

  clear: () => {
    careApi.clearSession();
    set({
      urlToken: null,
      catName: null,
  catSex: null,
      catId: null,
      carePassId: null,
      delegateName: null,
      scopes: [],
      weightUnit: DEFAULT_WEIGHT_UNIT,
      startsAt: null,
      endsAt: null,
      isValidated: false,
      isLoading: false,
      error: null,
      errorCode: null,
      recentLogs: [],
      medications: [],
    });
  },
}));
